import { useCallback, useEffect, useRef, useState } from 'react';
import type { BarChart, PieChart, ChartData } from '@trinity/chat';
import { LegendCollapseToggleContext, ChartInfoContext, ChartGroupByContext, ChartTypeContext, ChartViewContext, ChartRecordIdContext } from '../Chat/context';
import { mapChartTypeToBackend } from './Chat.Chart.utils';
import { useCreateChartRecordMutation } from './hooks/useCreateChartPreferenceMutation';

type Props = {
  chart: ChartData;
  children: React.ReactNode;
};

// converting bar chart data to a pie chart data
export const barToPie = (data: BarChart): PieChart => {
  const pieData: PieChart['data'] = [];

  for (let i = 0; i < data.data.length; i++) {
    pieData.push({
      id: data.data[i].x,
      label: data.data[i].x,
      value: data.data[i].y,
    });
  }

  return {
    title: data.title,
    xAxisTitle: data.xAxisTitle,
    yAxisTitle: data.yAxisTitle,
    data: pieData,
  };
};

export const pieToBar = (data: PieChart): BarChart => {
  const barData: BarChart['data'] = [];

  for (let i = 0; i < data.data.length; i++) {
    barData.push({
      x: data.data[i].label,
      y: data.data[i].value,
    });
  }

  return {
    indexBy: data.xAxisTitle,
    title: data.title,
    xAxisTitle: data.xAxisTitle,
    yAxisTitle: data.yAxisTitle,
    data: barData,
  };
};

export const ChartLegendContainer = (props: {
  children: React.ReactNode;
  defaultValue?: boolean;
}) => {
  const [open, setOpen] = useState(props.defaultValue);
  return (
    <LegendCollapseToggleContext.Provider value={[open, setOpen]}>
      {props.children}
    </LegendCollapseToggleContext.Provider>
  );
};

export const ChartContainer = (props: Props) => {

  const [chart, setChart] = useState<ChartData>(props.chart);
  const [groupBy, setGroupBy] = useState<number | null>(null);
  const [chartType, setChartType] = useState<string | null>(props.chart?.chart?.type);
  const chartRecordIdRef = useRef<number | null>(null);
  const chartRecordedRef = useRef<boolean>(false);
  const ChartView = useState<'chart' | 'table'>('chart');

  const createChartRecordMutation = useCreateChartRecordMutation({
    onSuccess: response => {
      chartRecordIdRef.current = response.record.id;
      chartRecordedRef.current = true;
    },
    onError: error => {
      console.warn('Failed to record initial chart creation:', error);
    },
  });

  const saveChartPreferences = useCallback(() => {
    if (chartRecordedRef.current || !chartType || createChartRecordMutation.isPending) {
      return;
    }

    // Map the chart type to our backend enum
    const mappedChartType = mapChartTypeToBackend(chartType);
    if (!mappedChartType) {
      return;
    }

    createChartRecordMutation.mutateAsync({
      initialChartType: mappedChartType,
    });
  }, [chartType, createChartRecordMutation]);

  // Record initial chart creation
  useEffect(() => {
    saveChartPreferences();
  }, [saveChartPreferences]);

  return (
    <ChartLegendContainer>
      <ChartInfoContext.Provider value={[chart, setChart]}>
        <ChartGroupByContext.Provider value={[groupBy, setGroupBy]}>
          <ChartTypeContext.Provider value={[chartType, setChartType]}>
            <ChartViewContext.Provider value={ChartView}>
              <ChartRecordIdContext.Provider value={chartRecordIdRef.current}>
                {props.children}
              </ChartRecordIdContext.Provider>
            </ChartViewContext.Provider>
          </ChartTypeContext.Provider>
        </ChartGroupByContext.Provider>
      </ChartInfoContext.Provider>
    </ChartLegendContainer>
  );
};